import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

interface BatchChoice {
  id: string
  name: string
}

export interface BatchResult {
  id: string
  name: string
  adventurers: any[]
}

interface FetchParams {
  country?: number
  outtalent_program_status?: string
  exclude_paused?: boolean
  page?: number
  page_size?: number
}

interface FetchOptions {
  exclude_paused?: boolean
}

export const useAdventurersStore = defineStore('adventurers', () => {
  // State
  const batchChoices = ref<BatchChoice[]>([])
  const batches = ref<any[]>([])
  const searchResults = ref<BatchResult[]>([])
  const selectedBatchData = ref<any | null>(null)
  const allFellows = ref<any[]>([])
  const allFellowsNextUrl = ref<string | null>(null)
  const isLoadingMore = ref(false)
  const isLoading = ref(false)
  const isSearching = ref(false)

  const error = ref<string | null>(null)
  
  // Track the latest search request ID to prevent race conditions
  let lastSearchRequestId = 0

  const formatBatchName = (batchId: string) => {
    return batchId
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const groupAdventurersByBatch = (results: any[]) => {
    return results.reduce((acc: BatchResult[], adventurer: any) => {
      const batchId = adventurer.batch
      const existingBatch = acc.find((b: BatchResult) => b.id === batchId)

      if (existingBatch) {
        existingBatch.adventurers.push(adventurer)
      } else {
        acc.push({
          id: batchId,
          name: formatBatchName(batchId),
          adventurers: [adventurer]
        })
      }
      return acc
    }, [])
  }

  // Actions
  const fetchBatchChoices = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_BATCH_CHOICES)
      batchChoices.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch batch choices'
      console.error('Error fetching batch choices:', err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllBatches = async (options: FetchOptions = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_ADVENTURERS_BY_BATCH, {
        params: {
          ...(options.exclude_paused !== undefined && { exclude_paused: options.exclude_paused })
        }
      })
      // Ensure each adventurer has their batch information and format batch names
      batches.value = response.data.map((batch: any) => ({
        ...batch,
        name: formatBatchName(batch.id),
        adventurers: batch.adventurers.map((adventurer: any) => ({
          ...adventurer,
          batch: batch.id // Ensure batch ID is set for each adventurer
        }))
      }))
      selectedBatchData.value = null
      return batches.value
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch all batches'
      console.error('Error fetching all batches:', err)
      batches.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const normalizeAllFellows = (results: any[]) => {
    return results.map((fellow) => {
      if (fellow?.profile_details?.user_details) {
        return fellow
      }

      const user = fellow?.user || {}
      return {
        ...fellow,
        profile_details: {
          ...fellow?.profile_details,
          user_details: {
            ...user
          },
          linkedin_url: user.linkedin_profile_url || fellow?.profile_details?.linkedin_url,
          telegram_handle: user.telegram_username || fellow?.profile_details?.telegram_handle
        }
      }
    })
  }

  const fetchAllAdventurers = async (filters: {
    expedition?: number
    outtalent_program_status?: string
    outtalent_relationship?: string
    country?: number
    q?: string
  } = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const params: Record<string, string | number> = {}
      if (filters.expedition !== undefined) {
        params.expedition = filters.expedition
      }
      if (filters.outtalent_program_status) {
        params.outtalent_program_status = filters.outtalent_program_status
      }
      if (filters.outtalent_relationship) {
        params.outtalent_relationship = filters.outtalent_relationship
      }
      if (filters.country !== undefined) {
        params.country = filters.country
      }
      if (filters.q) {
        params.q = filters.q
      }

      const response = await api.get(API_ROUTES.GET_ALL_FELLOWS_PAGINATED, { params })
      const results = response.data?.results || response.data || []
      allFellows.value = normalizeAllFellows(results)
      allFellowsNextUrl.value = response.data?.next ?? null
      batches.value = []
      selectedBatchData.value = null
      searchResults.value = []
      return allFellows.value
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch all fellows'
      console.error('Error fetching all fellows:', err)
      allFellows.value = []
      allFellowsNextUrl.value = null
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllFellowsPaginated = async (filters: {
    outtalent_program_status?: string
    outtalent_relationship?: string
    country?: number
  } = {}) => {
    return fetchAllAdventurers(filters)
  }

  const fetchMoreFellows = async () => {
    if (!allFellowsNextUrl.value || isLoadingMore.value) return []

    isLoadingMore.value = true
    error.value = null

    try {
      // Ensure the URL uses HTTPS to avoid mixed content errors
      const nextUrl = allFellowsNextUrl.value.replace(/^http:\/\//i, 'https://')
      const response = await api.get(nextUrl)
      const results = response.data?.results || response.data || []
      const normalized = normalizeAllFellows(results)
      allFellows.value = [...allFellows.value, ...normalized]
      allFellowsNextUrl.value = response.data?.next ?? null
      return normalized
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch more fellows'
      console.error('Error fetching more fellows:', err)
      return []
    } finally {
      isLoadingMore.value = false
    }
  }

  const clearAllFellows = () => {
    allFellows.value = []
    allFellowsNextUrl.value = null
  }

  const fetchAdventurersByBatch = async (batchId: string, options: FetchOptions = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const formattedBatchId = batchId
        .toUpperCase()
        .replace(/ /g, '_')
        .replace(/Expedition/i, 'EXPEDITION')
      
      const response = await api.get(API_ROUTES.GET_ADVENTURERS_BY_BATCH_ID(formattedBatchId), {
        params: {
          ...(options.exclude_paused !== undefined && { exclude_paused: options.exclude_paused })
        }
      })
      selectedBatchData.value = response.data
      batches.value = []
      searchResults.value = [] // Clear search results when fetching batch data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch adventurers'
      console.error('Error fetching adventurers:', err)
      selectedBatchData.value = null
      return []
    } finally {
      isLoading.value = false
    }
  }

  const searchAdventurers = async (query: string, params?: FetchParams) => {
    if (!query.trim()) {
      searchResults.value = []
      return
    }

    lastSearchRequestId++
    const requestId = lastSearchRequestId

    isSearching.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_FELLOW_DATA, {
        params: {
          q: query,
          page_size: 20, // Limit to 20 as requested
          ...params
        }
      })

      // If this is not the latest request, ignore the results
      if (requestId !== lastSearchRequestId) {
        return
      }

      // Handle paginated response format
      const results = response.data.results || response.data

      // Group search results by batch
      const resultsByBatch = groupAdventurersByBatch(results || [])

      searchResults.value = sortBatches(resultsByBatch)
    } catch (err: any) {
      // Only set error if this is the latest request
      if (requestId === lastSearchRequestId) {
        error.value = err.response?.data?.message || 'Failed to search adventurers'
        console.error('Error searching adventurers:', err)
        searchResults.value = []
      }
    } finally {
      // Only set loading to false if this is the latest request
      if (requestId === lastSearchRequestId) {
        isSearching.value = false
      }
    }
  }

  const fetchAdventurersByCountry = async (countryId: number) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_FELLOW_DATA, {
        params: {
          country: countryId
        }
      })

      // Clear other state to prevent showing stale data
      batches.value = []
      selectedBatchData.value = null

      // Handle paginated response format
      const results = response.data.results || response.data

      // If no results, just set empty arrays and return
      if (!results || results.length === 0) {
        searchResults.value = []
        return []
      }

      // Group results by batch
      const resultsByBatch = groupAdventurersByBatch(results)

      searchResults.value = sortBatches(resultsByBatch)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch adventurers by country'
      console.error('Error fetching adventurers by country:', err)
      searchResults.value = []
    } finally {
      isLoading.value = false
    }
  }

  const fetchAdventurersByProgramStatus = async (programStatus: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_FELLOW_DATA, {
        params: {
          outtalent_program_status: programStatus
        }
      })

      // Clear other state to prevent showing stale data
      batches.value = []
      selectedBatchData.value = null

      // Handle paginated response format
      const results = response.data.results || response.data

      // If no results, just set empty arrays and return
      if (!results || results.length === 0) {
        searchResults.value = []
        return []
      }

      // Group results by batch
      const resultsByBatch = groupAdventurersByBatch(results)

      searchResults.value = sortBatches(resultsByBatch)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch adventurers by program status'
      console.error('Error fetching adventurers by program status:', err)
      searchResults.value = []
    } finally {
      isLoading.value = false
    }
  }

  const clearSearch = () => {
    searchResults.value = []
  }

  const clearSelectedBatch = () => {
    selectedBatchData.value = null
  }

  // Helper function to sort batches by expedition number
  const sortBatches = (batches: BatchResult[]) => {
    return batches.sort((a, b) => {
      // Extract year and expedition number
      const yearA = parseInt(a.id.match(/\d{4}/)?.[0] || '0')
      const yearB = parseInt(b.id.match(/\d{4}/)?.[0] || '0')
      
      // If years are different, sort by year first
      if (yearA !== yearB) {
        return yearB - yearA
      }
      
      // If years are same, sort by expedition number
      const numA = parseInt(a.id.match(/\d+/g)?.pop() || '0')
      const numB = parseInt(b.id.match(/\d+/g)?.pop() || '0')
      return numB - numA
    })
  }

  return {
    // State
    batchChoices,
    batches,
    searchResults,
    selectedBatchData,
    allFellows,
    allFellowsNextUrl,
    isLoadingMore,
    isLoading,
    isSearching,
    error,

    // Actions
    fetchBatchChoices,
    fetchAllBatches,
    fetchAllAdventurers,
    fetchAllFellowsPaginated,
    fetchMoreFellows,
    fetchAdventurersByBatch,
    searchAdventurers,
    fetchAdventurersByCountry,
    fetchAdventurersByProgramStatus,
    clearSearch,
    clearSelectedBatch,
    clearAllFellows
  }
}) 